import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createKeyedAsyncOnce } from './once'
import { getSafeReturnTo, oidcConfig, userManager, type OidcUser } from './oidc'

type AuthStatus = 'checking' | 'authenticated' | 'signed-out'

type AuthContextValue = {
  status: AuthStatus
  user: OidcUser | null
  configured: boolean
  signingIn: boolean
  signingOut: boolean
  globalLogoutConfigured: boolean
  signIn: (returnTo?: string) => Promise<void>
  completeSignIn: () => Promise<string>
  signOut: () => Promise<void>
  globalSignOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const completeCallbackOnce = createKeyedAsyncOnce(async (callbackUrl) => {
  if (!userManager) throw new Error('OIDC is not configured')
  return userManager.signinRedirectCallback(callbackUrl)
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(
    oidcConfig.configured ? 'checking' : 'signed-out',
  )
  const [user, setUser] = useState<OidcUser | null>(null)
  const [signingIn, setSigningIn] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const signInFlight = useRef(false)
  const signOutFlight = useRef(false)

  const publish = useCallback((candidate: OidcUser | null) => {
    const active = candidate && !candidate.expired ? candidate : null
    setUser(active)
    setStatus(active ? 'authenticated' : 'signed-out')
  }, [])

  useEffect(() => {
    if (!userManager) {
      return
    }
    const manager = userManager
    const loaded = (candidate: OidcUser) => publish(candidate)
    const unloaded = () => publish(null)
    const expired = () => {
      void manager.removeUser()
      publish(null)
    }
    void manager
      .getUser()
      .then(async (candidate) => {
        if (candidate?.expired) await manager.removeUser()
        publish(candidate?.expired ? null : candidate)
      })
      .catch(() => publish(null))
    manager.events.addUserLoaded(loaded)
    manager.events.addUserUnloaded(unloaded)
    manager.events.addAccessTokenExpired(expired)
    return () => {
      manager.events.removeUserLoaded(loaded)
      manager.events.removeUserUnloaded(unloaded)
      manager.events.removeAccessTokenExpired(expired)
    }
  }, [publish])

  const signIn = useCallback(async (returnTo = '/') => {
    if (!userManager || signInFlight.current) return
    signInFlight.current = true
    setSigningIn(true)
    try {
      await userManager.signinRedirect({
        state: { returnTo: getSafeReturnTo(returnTo) },
      })
    } finally {
      signInFlight.current = false
      setSigningIn(false)
    }
  }, [])

  const completeSignIn = useCallback(async () => {
    try {
      const candidate = await completeCallbackOnce(window.location.href)
      publish(candidate)
      const state = candidate.state as { returnTo?: unknown } | undefined
      await Promise.resolve()
      return getSafeReturnTo(state?.returnTo)
    } catch (error) {
      publish(null)
      await userManager?.clearStaleState().catch(() => undefined)
      throw error
    }
  }, [publish])

  const signOut = useCallback(async () => {
    if (!userManager || signOutFlight.current) return
    signOutFlight.current = true
    setSigningOut(true)
    const current = await userManager.getUser().catch(() => null)
    await userManager.removeUser().catch(() => undefined)
    publish(null)
    try {
      await userManager.signoutRedirect({ id_token_hint: current?.id_token })
    } catch {
      window.location.assign('/auth')
    } finally {
      signOutFlight.current = false
      setSigningOut(false)
    }
  }, [publish])

  const globalSignOut = useCallback(async () => {
    if (signOutFlight.current) return
    signOutFlight.current = true
    setSigningOut(true)
    await userManager?.removeUser().catch(() => undefined)
    publish(null)
    if (oidcConfig.configured && oidcConfig.globalLogoutUri) {
      window.location.assign(oidcConfig.globalLogoutUri)
    } else {
      window.location.assign('/auth')
    }
  }, [publish])

  const value = useMemo(
    () => ({
      status,
      user,
      configured: oidcConfig.configured,
      signingIn,
      signingOut,
      globalLogoutConfigured: oidcConfig.configured && Boolean(oidcConfig.globalLogoutUri),
      signIn,
      completeSignIn,
      signOut,
      globalSignOut,
    }),
    [status, user, signingIn, signingOut, signIn, completeSignIn, signOut, globalSignOut],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within AuthProvider')
  return value
}
